/*
 * Studio v2 — core: the slot model, the rules layer, the screen registry, the router and the
 * entry presets. No DOM: this file runs in the browser (window.StudioCore) and in Node tests
 * (scripts/studio-core.test.mjs).
 *
 *   SLOTS     The order is a set of slots. Each slot is { status, value, source, at }:
 *             status  'empty' | 'proposed' | 'confirmed'
 *             source  'preset' (ad / URL) | 'ai' | 'shopper' | 'default'
 *   RULES     validate(update) runs before ANY update is applied: it can reject it, adjust it
 *             (e.g. clamp to stock) and cascade other updates (a new style remaps the colorway and
 *             the print locations). defaults() fill every empty slot, so any order can reach
 *             checkout. gates() are the hard constraints checked at checkout (e.g. the screen-print
 *             minimum), each with a one-tap fix where one exists.
 *   SCREENS   Modules registered with register(). Contract: see SCREEN_FIELDS below.
 *   ROUTER    route(state, ctx): the highest-priority relevant core screen that still has an
 *             unconfirmed slot (or is terminal), plus the relevant side screens as offers.
 *   PRESETS   presetsFromUrl(params, catalog): URL parameters → confirmed slots with source
 *             'preset' (e.g. ?style=hoodie&color=Navy&method=screen&qty=24).
 */
(function (root) {
  const SLOTS = ['style', 'colorway', 'decorations', 'sizes', 'sample', 'method', 'contact'];
  const STATUSES = ['empty', 'proposed', 'confirmed'];
  const SOURCES = ['preset', 'ai', 'shopper', 'default'];
  const METHODS = ['digital', 'screen'];
  const SPLIT = { XS: 0.04, S: 0.14, M: 0.28, L: 0.26, XL: 0.18, '2XL': 0.07, '3XL': 0.03 };
  const MAX_INKS = 5;

  // ------------------------------------------------------------------------------ catalog
  // The theme hands over raw product JSON (sections/main-studio.liquid); this turns it into the
  // shape the rules use. `zonesOf(product)` lists a product's location keys (DecorateZones in the
  // browser; a stub in tests).
  function family(p) {
    const h = `${p.handle} ${p.title} ${p.type || ''} ${p.group || ''} ${p.styleGroup || ''}`.toLowerCase();
    if (h.includes('blanket')) return 'blanket';
    if (h.includes('vest') || h.includes('q zip') || h.includes('q-zip') || h.includes('quarter')) return 'layer';
    if (h.includes('hood')) return 'hood';
    if (h.includes('crew') || h.includes('sweatshirt')) return 'crew';
    if (h.includes('tank')) return 'tank';
    return 'tee';
  }
  const ALIASES = {
    hood: 'hood', hoodie: 'hood', hoodies: 'hood', hoody: 'hood', sweatshirt: 'crew', crew: 'crew', crewneck: 'crew',
    tee: 'tee', tees: 'tee', tshirt: 'tee', 't-shirt': 'tee', shirt: 'tee', tank: 'tank', blanket: 'blanket', blankets: 'blanket',
    qzip: 'layer', 'q-zip': 'layer', quarterzip: 'layer', vest: 'layer', layer: 'layer',
  };

  function buildTypes(raw) {
    return (raw || []).map((t) => {
      const services = [];
      (t.variants || []).forEach((v) => {
        const label = String(v.service || '').replace(/\s*\([A-Z0-9]+\)\s*$/, '');
        let sv = services.find((x) => x.code === v.code);
        if (!sv) services.push((sv = { code: v.code, label, breaks: [] }));
        sv.breaks.push({ id: v.id, min: v.min, price: v.price, available: v.available });
      });
      services.forEach((sv) => sv.breaks.sort((x, y) => x.min - y.min));
      return { ...t, services: services.filter((sv) => sv.breaks.length) };
    }).filter((t) => t.services.length).sort((x, y) => x.sort - y.sort);
  }

  // Sizes smallest to largest (bug 5, sweep 2026-10-21: the catalog lists XS after 2XL, so every grid read
  // S, M, L, XL, 2XL, XS, 3XL). Sizes not on the list (one size, youth) keep their place after the known ones.
  const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', '6XL'];
  function sortSizes(list) {
    const rank = (x) => { const i = SIZE_ORDER.indexOf(String(x && x.size != null ? x.size : x).toUpperCase()); return i < 0 ? 99 : i; };
    return list.map((x, i) => [x, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map((a) => a[0]);
  }
  function makeCatalog(data, zonesOf) {
    const types = buildTypes(data.types);
    const products = (data.products || []).map((p) => {
      const mine = p.typeHandles || [];
      const ptypes = mine.length ? types.filter((t) => mine.includes(t.handle)) : types.filter((t) => t.general);
      const sizes = sortSizes(p.sizes && p.sizes.length ? p.sizes : [...new Set(p.variants.map((v) => v.size))]);
      const minPrice = Math.min(...p.variants.map((v) => v.price));
      return { ...p, family: family(p), types: ptypes, sizes, minPrice, zoneKeys: zonesOf ? zonesOf(p) : (p.zoneKeys || []) };
    });
    return { products, types, setupFee: data.setupFee || null, inks: data.inks || [], defaultHandle: data.defaultHandle || null };
  }
  const product = (cat, h) => cat.products.find((p) => p.handle === h) || null;
  const colorNames = (p) => (p ? p.colors.map((c) => c.name) : []);
  // A colourway is in stock when any of its sizes can be bought (2026-10-11: the AI offered sold-out
  // Stardust blankets, which priced at $0.00 and led to a work order with no size to choose).
  function colorAvailable(p, name) {
    return !!p && p.variants.some((v) => (v.color == null || v.color === name) && v.available);
  }
  const availableColors = (p) => colorNames(p).filter((n) => colorAvailable(p, n));
  function defaultColor(p) {
    const names = availableColors(p).length ? availableColors(p) : colorNames(p);
    return ['Navy', 'Black', 'Red'].find((n) => names.includes(n)) || names[0] || null;
  }
  function defaultSize(p, color) {
    const vs = p.variants.filter((v) => (v.color == null || v.color === color) && v.available);
    const order = p.sizes.filter((s) => vs.some((v) => v.size === s));
    return ['L', 'M', 'XL', 'S'].find((s) => order.includes(s)) || order[Math.floor(order.length / 2)] || p.sizes[0] || null;
  }
  // The decoration type behind a method on a product: screen = an ink-based type; digital = a
  // type without inks whose first break starts at 1 piece (Color Direct).
  function methodType(p, method) {
    if (!p) return null;
    if (method === 'screen') return p.types.find((t) => t.ink) || null;
    return p.types.find((t) => !t.ink && t.services.some((sv) => sv.breaks[0].min <= 1)) || p.types.find((t) => !t.ink) || null;
  }
  // The same place on another garment: a front print stays a front print.
  const EQUIV = { FC: ['FC', 'FCZC', 'BKT', 'BCT'], FCZC: ['FCZC', 'FC', 'BKT', 'BCT'], BKT: ['BKT', 'BCT', 'FC', 'FCZC'], BCT: ['BCT', 'BKT', 'FC', 'FCZC'], LC: ['LC', 'RC', 'FC', 'FCZC', 'BKT', 'BCT'], RC: ['RC', 'LC', 'FC', 'BKT'], FB: ['FB', 'FC', 'BKT', 'BCT'] };
  function mapLocation(p, key) {
    const keys = p.zoneKeys || [];
    if (keys.includes(key)) return key;
    const want = EQUIV[key] || [key, 'FC', 'FCZC', 'BKT', 'BCT'];
    return want.find((k) => keys.includes(k)) || keys[0] || null;
  }

  // ------------------------------------------------------------------------------ state
  function emptySlot() { return { status: 'empty', value: null, source: null, at: null }; }
  function createState() {
    const slots = {};
    SLOTS.forEach((k) => (slots[k] = emptySlot()));
    return { slots, proposals: null, read: '', brief: { text: '', image: null }, side: null, history: [], notes: [], log: [] };
  }
  const filled = (s) => s && s.status !== 'empty';
  const confirmed = (s) => s && s.status === 'confirmed';

  // ------------------------------------------------------------------------------ rules
  // Every update goes through validate() before it is applied. An update is
  // { slot, value, status?, source? }; status defaults to 'confirmed', source to 'shopper'.
  // Returns { ok, update, reason, cascade: [updates] }.
  function validate(state, upd, cat) {
    const u = { status: 'confirmed', source: 'shopper', ...upd };
    if (!SLOTS.includes(u.slot)) return { ok: false, reason: `unknown slot ${u.slot}` };
    if (!STATUSES.includes(u.status)) return { ok: false, reason: `bad status ${u.status}` };
    if (!SOURCES.includes(u.source)) return { ok: false, reason: `bad source ${u.source}` };
    if (u.status === 'empty') return { ok: true, update: { ...u, value: null, source: null }, cascade: [] };
    const eff = effective(state, cat);
    const stylePending = state.pending && state.pending.style;
    const p = product(cat, u.slot === 'style' ? u.value : stylePending || eff.style.value);
    const cascade = [];
    switch (u.slot) {
      case 'style': {
        if (!p) return { ok: false, reason: `We do not make "${u.value}".` };
        // The colorway and the print locations follow the garment.
        const color = eff.colorway.value;
        if (filled(state.slots.colorway) && !colorAvailable(p, color)) {
          cascade.push({ slot: 'colorway', value: defaultColor(p), status: 'proposed', source: 'default', note: `${color} is not ${colorNames(p).includes(color) ? 'in stock' : 'made'} in ${p.title}; showing ${defaultColor(p)}.` });
        }
        const decs = state.slots.decorations.value || [];
        if (decs.length) {
          const seen = new Set();
          const moved = decs.map((d) => ({ ...d, location: mapLocation(p, d.location) })).filter((d) => d.location && !seen.has(d.location) && seen.add(d.location));
          if (moved.length !== decs.length || moved.some((d, i) => d.location !== decs[i].location)) cascade.push({ slot: 'decorations', value: moved, status: state.slots.decorations.status, source: state.slots.decorations.source });
        }
        if (filled(state.slots.sizes)) {
          const sizes = Object.fromEntries(Object.entries(state.slots.sizes.value || {}).filter(([s]) => p.sizes.includes(s)));
          if (Object.keys(sizes).length !== Object.keys(state.slots.sizes.value || {}).length) cascade.push({ slot: 'sizes', value: sizes, status: 'proposed', source: 'default' });
        }
        if (filled(state.slots.method) && !methodType(p, state.slots.method.value)) cascade.push({ slot: 'method', value: 'digital', status: 'proposed', source: 'default', note: `${p.title} is printed in full color (digital).` });
        return { ok: true, update: u, cascade };
      }
      case 'colorway': {
        if (!p) return { ok: false, reason: 'Pick a garment first.' };
        if (!colorNames(p).includes(u.value)) return { ok: false, reason: `${p.title} does not come in ${u.value}.` };
        if (!colorAvailable(p, u.value)) return { ok: false, reason: `${p.title} in ${u.value} is sold out. Pick another colour.` };
        return { ok: true, update: u, cascade };
      }
      case 'decorations': {
        const decs = Array.isArray(u.value) ? u.value : [];
        if (!p) return { ok: false, reason: 'Pick a garment first.' };
        const keys = new Set();
        for (const d of decs) {
          if (!d || !d.location || !d.design) return { ok: false, reason: 'Each decoration needs a location and a design.' };
          if (!(p.zoneKeys || []).includes(d.location)) return { ok: false, reason: `${p.title} has no print location ${d.location}.` };
          if (keys.has(d.location)) return { ok: false, reason: 'One design per location.' };
          const k = d.design.kind;
          if (!['upload', 'text', 'art'].includes(k)) return { ok: false, reason: `unknown design kind ${k}` };
          if (k === 'text' && !String(d.design.text || '').trim()) return { ok: false, reason: 'The lettering is empty.' };
          if ((k === 'upload' || k === 'art') && !d.design.src) return { ok: false, reason: 'The design image is not ready yet.' };
          keys.add(d.location);
        }
        const conflicts = (cat.conflicts || {});
        for (const k of keys) for (const c of conflicts[k] || []) if (keys.has(c)) return { ok: false, reason: `${k} and ${c} overlap; pick one.` };
        return { ok: true, update: { ...u, value: decs }, cascade };
      }
      case 'sizes': {
        if (!p) return { ok: false, reason: 'Pick a garment first.' };
        const color = eff.colorway.value, out = {};
        let note = '';
        for (const [s, n0] of Object.entries(u.value || {})) {
          const n = Math.max(0, Math.floor(+n0 || 0));
          if (!n) continue;
          const v = p.variants.find((x) => x.size === s && (x.color == null || x.color === color));
          if (!v) return { ok: false, reason: `${p.title} has no size ${s} in ${color}.` };
          if (!v.available) return { ok: false, reason: `${s} is sold out in ${color}.` };
          out[s] = v.stock != null && n > v.stock ? ((note = `Only ${v.stock} ${s} in stock in ${color}; set to ${v.stock}.`), v.stock) : n;
        }
        const pieces = Object.values(out).reduce((a, n) => a + n, 0);
        if (eff.sample.value && pieces > 1) return { ok: false, reason: 'A sample is one piece. Turn the sample off to order more.' };
        return { ok: true, update: { ...u, value: out, note }, cascade };
      }
      case 'sample': {
        const on = !!u.value;
        if (on) {
          if (eff.method.value === 'screen') cascade.push({ slot: 'method', value: 'digital', status: 'confirmed', source: 'default', note: 'Samples are printed digitally (Color Direct).' });
          const sz = eff.sizes.value || {}, first = Object.keys(sz).find((s) => sz[s] > 0) || (p && defaultSize(p, eff.colorway.value));
          if (first && Object.values(sz).reduce((a, n) => a + n, 0) !== 1) cascade.push({ slot: 'sizes', value: { [first]: 1 }, status: 'proposed', source: 'default' });
        }
        return { ok: true, update: { ...u, value: on }, cascade };
      }
      case 'method': {
        if (!METHODS.includes(u.value)) return { ok: false, reason: `unknown method ${u.value}` };
        if (u.value === 'screen' && eff.sample.value) return { ok: false, reason: 'Samples are printed digitally. Turn the sample off for screen printing.' };
        if (p && !methodType(p, u.value)) return { ok: false, reason: `${p.title} is not ${u.value === 'screen' ? 'screen printed' : 'printed digitally'}.` };
        return { ok: true, update: u, cascade };
      }
      case 'contact': {
        const v = u.value || {};
        const email = String(v.email || '').trim(), sms = String(v.sms || '').trim();
        if (!email && !sms) return { ok: false, reason: 'Give an email or a mobile number.' };
        if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return { ok: false, reason: 'That email does not look right.' };
        if (sms && sms.replace(/\D/g, '').length < 10) return { ok: false, reason: 'A mobile number needs 10 digits.' };
        return { ok: true, update: { ...u, value: { email, sms } }, cascade };
      }
    }
    return { ok: false, reason: 'unhandled' };
  }

  // Apply a list of updates through the rules. Cascades are validated and applied too (depth 3).
  // Returns { state, applied: [updates], rejected: [{update, reason}], notes: [strings] }.
  function apply(state, updates, cat, now = Date.now()) {
    let s = { ...state, slots: { ...state.slots } };
    const applied = [], rejected = [], notes = [];
    const queue = updates.map((u) => [u, 0]);
    // Updates in one emission are validated against each other: a style change in the same batch
    // is what a colorway or a location in that batch is checked against.
    const styleUpd = updates.find((u) => u.slot === 'style');
    if (styleUpd) s.pending = { style: styleUpd.value };
    while (queue.length) {
      const [u, depth] = queue.shift();
      const r = validate(s, u, cat);
      if (!r.ok) { rejected.push({ update: u, reason: r.reason }); continue; }
      const x = r.update;
      s.slots[x.slot] = { status: x.status, value: x.value, source: x.source, at: now };
      applied.push(x);
      if (x.note) notes.push(x.note);
      if (u.note) notes.push(u.note);
      // A cascade never overrides a slot the same batch sets explicitly.
      if (depth < 3) r.cascade.filter((c) => !updates.some((o) => o !== u && o.slot === c.slot)).forEach((c) => queue.push([c, depth + 1]));
    }
    delete s.pending;
    s.log = (state.log || []).concat(applied.map((a) => ({ at: now, slot: a.slot, status: a.status, source: a.source }))).slice(-200);
    return { state: s, applied, rejected, notes };
  }

  // Smart defaults: what an empty slot means. Returned as slot-shaped objects with
  // source 'default' and status 'proposed', so the UI can say "(default)".
  function defaults(state, cat) {
    const sl = state.slots;
    const p = product(cat, sl.style.value) || product(cat, cat.defaultHandle) || cat.products[0] || null;
    const color = sl.colorway.value && p && colorNames(p).includes(sl.colorway.value) ? sl.colorway.value : defaultColor(p);
    const d = (value) => ({ status: 'proposed', value, source: 'default', at: null });
    return {
      style: d(p ? p.handle : null),
      colorway: d(color),
      decorations: d([]),
      // A group visitor (a group ad or entry, studio-app.js groupVisitor) starts at 12 pieces, the count the ads price;
      // a sample is always one piece.
      sizes: d(p ? { [defaultSize(p, color)]: sl.sample.value ? 1 : state.defaultQty || 1 } : {}),
      sample: d(false),
      method: d('digital'),
      contact: d(null),
    };
  }
  function effective(state, cat) {
    const def = defaults(state, cat), out = {};
    SLOTS.forEach((k) => (out[k] = filled(state.slots[k]) ? state.slots[k] : def[k]));
    return out;
  }

  // ------------------------------------------------------------------------------ pricing
  // Same arithmetic as the live Studio (assets/decorate.js quote()): blanks at the variant price,
  // plus one decoration fee per location per piece at the break the order reaches. Screen print
  // bills by ink count (service n = n inks); its minimum is that service's first break.
  function breakFor(sv, pieces) {
    let b = sv.breaks[0];
    sv.breaks.forEach((x) => { if (pieces >= x.min) b = x; });
    return b;
  }
  // The delivered price of one piece when n are ordered (2026-10-19 revamp: "one price everywhere"): the
  // garment's lowest price plus the full-color fee at the tier n reaches. Delivery is free on every order.
  function deliveredPrice(p, n) {
    const t = methodType(p, 'digital');
    if (!p || !t) return null;
    return p.minPrice + breakFor(t.services[0], n).price;
  }
  // "Mon, Oct 26" from the AI Worker's /api/ship arrivesBy (YYYY-MM-DD). The Studio, its footer and the cart all show
  // this one date (2026-10-19 revamp: "the same date everywhere").
  function shipDay(isoDay) {
    try { return new Date(`${isoDay}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }); } catch (_) { return isoDay; }
  }
  function inksOf(d) { return Math.max(1, Math.min(MAX_INKS, +(d.design && d.design.inks) || 1)); }
  function quote(eff, cat, override) {
    const e = override ? { ...eff, ...override } : eff;
    const p = product(cat, e.style.value);
    if (!p) return null;
    const color = e.colorway.value, method = e.method.value;
    const t = methodType(p, method);
    const vs = p.variants.filter((v) => v.color == null || v.color === color);
    const lines = p.sizes.map((s) => vs.find((v) => v.size === s)).filter((v) => v && v.available && (e.sizes.value || {})[v.size] > 0).map((v) => ({ v, n: e.sizes.value[v.size] }));
    const pieces = lines.reduce((a, l) => a + l.n, 0);
    const blank = lines.reduce((a, l) => a + l.n * l.v.price, 0);
    const decs = e.decorations.value || [];
    const fees = t ? decs.map((d) => {
      const sv = t.ink ? t.services[Math.min(t.services.length, inksOf(d)) - 1] : t.services[0];
      return { location: d.location, sv, fee: breakFor(sv, pieces), min: sv.breaks[0].min };
    }) : [];
    const deco = fees.reduce((a, x) => a + x.fee.price * pieces, 0);
    const setup = t && t.setup && fees.length && cat.setupFee ? cat.setupFee.price : 0;
    const minimum = e.sample.value ? 1 : Math.max(1, ...fees.map((x) => x.min));
    const unit = pieces ? Math.round((blank + deco + setup) / pieces) : 0;
    return { product: p, type: t, method, lines, pieces, blank, fees, deco, setup, minimum, total: blank + deco + setup, unit };
  }
  // Sizes for n pieces with a typical team split, capped by stock (as the live "Fill to N").
  function split(p, color, n, base = {}) {
    const vs = p.variants.filter((v) => (v.color == null || v.color === color) && v.available);
    const avail = p.sizes.filter((s) => vs.some((v) => v.size === s));
    if (!avail.length) return {};
    const cap = (s) => { const v = vs.find((x) => x.size === s); return v && v.stock != null ? v.stock : Infinity; };
    const q = { ...base };
    let have = Object.values(q).reduce((a, x) => a + x, 0);
    const w = (s) => SPLIT[s] ?? 0.02, total = avail.reduce((a, s) => a + w(s), 0), need = n - have;
    if (need <= 0) return q;
    avail.forEach((s) => { const add = Math.min(cap(s) - (q[s] || 0), Math.floor((need * w(s)) / total)); if (add > 0) { q[s] = (q[s] || 0) + add; have += add; } });
    const order = avail.slice().sort((a, b) => w(b) - w(a));
    for (let i = 0, guard = 0; have < n && guard < 100000; guard++) { const s = order[i++ % order.length]; if ((q[s] || 0) < cap(s)) { q[s] = (q[s] || 0) + 1; have++; } }
    return q;
  }

  // Lettering nobody wrote: the words a new print spot starts with ("TEXT"), or the fallback "YOUR TEXT".
  // #MV1540 (2026-10-13) reached checkout with "TEXT" on the left chest of two garments: the shopper moved their
  // design from the full chest to the left chest, the new spot started as "TEXT", and nothing stopped it.
  const isPlaceholder = (design) => !!design && design.kind === 'text' && (!!design.placeholder || /^\s*(your\s+)?text(\s+here)?\s*$/i.test(String(design.text || '')));
  // A design whose every print is a placeholder has nothing of the shopper's in it: it must not reach the review
  // mockup or the model shot (2026-10-15, sid 01a14107: the AI was down, a fallback idea said "YOUR TEXT", the shopper
  // picked it and the model shot printed "YOUR TEXT" on a hoodie).
  const placeholderOnly = (decs) => Array.isArray(decs) && decs.length > 0 && decs.every((d) => d && isPlaceholder(d.design));
  const locationName = (p, code) => { const c = (p.cmsLocations || []).find((x) => x.code === code); return c && c.name ? String(c.name).replace(/\s*\(.*\)\s*$/, '') : code; };

  // Hard constraints, checked when the shopper checks out (and shown live on screens that care).
  // Each gate: { id, slot, message, fixes: [{ label, updates }] }.
  function gates(state, cat) {
    const eff = effective(state, cat), q = quote(eff, cat), out = [];
    if (!q) return [{ id: 'style', slot: 'style', message: 'Pick a garment.', fixes: [] }];
    const decs = eff.decorations.value || [];
    if (!decs.length && !eff.sample.value) out.push({ id: 'design', slot: 'decorations', message: 'Add a design, or order a blank sample.', fixes: [] });
    if (!q.type && decs.length) out.push({ id: 'method', slot: 'method', message: `${q.product.title} cannot be ${eff.method.value === 'screen' ? 'screen printed' : 'printed digitally'}.`, fixes: [] });
    if (q.pieces < 1) out.push({ id: 'pieces', slot: 'sizes', message: 'Choose at least one piece.', fixes: [{ label: `1 × ${defaultSize(q.product, eff.colorway.value)}`, updates: [{ slot: 'sizes', value: { [defaultSize(q.product, eff.colorway.value)]: 1 } }] }] });
    for (const d of decs.filter((x) => isPlaceholder(x.design))) {
      const name = locationName(q.product, d.location), mine = decs.find((x) => x !== d && !isPlaceholder(x.design));
      const fixes = [];
      if (mine) fixes.push({ id: 'placeholder-copy', label: `Put your design on the ${name}`, updates: [{ slot: 'decorations', value: decs.map((x) => (x === d ? { location: x.location, design: { ...mine.design } } : x)) }] });
      if (decs.length > 1) fixes.push({ id: 'placeholder-remove', label: `Remove the ${name} print`, updates: [{ slot: 'decorations', value: decs.filter((x) => x !== d) }] });
      out.push({ id: 'placeholder', slot: 'decorations', message: `The ${name} print still says “${String(d.design.text).trim()}”. Type your words there (Edit the design), or use your own design.`, fixes });
    }
    if (eff.sample.value && q.pieces !== 1) out.push({ id: 'sample', slot: 'sizes', message: 'A sample is one piece.', fixes: [] });
    if (q.type && decs.length && q.pieces >= 1 && q.pieces < q.minimum) {
      // The rule the 10-11 analysis found shoppers leaving on: offer digital at the same pieces.
      const dq = quote(eff, cat, { method: { ...eff.method, value: 'digital' } });
      const fixes = [];
      if (dq && dq.type && dq.minimum <= q.pieces) fixes.push({ id: 'to-digital', label: `Print ${q.pieces} in full color (digital): ${money(dq.total)}, no minimum`, updates: [{ slot: 'method', value: 'digital' }] });
      fixes.push({ id: 'fill', label: `Make it ${q.minimum} pieces`, updates: [{ slot: 'sizes', value: split(q.product, eff.colorway.value, q.minimum, eff.sizes.value) }] });
      out.push({ id: 'minimum', slot: 'sizes', message: `Screen printing starts at ${q.minimum} pieces. You have ${q.pieces}.`, fixes, digital: dq });
    }
    return out;
  }
  let moneyFormat = '${{amount}}';
  function money(c) {
    const v = (c / 100).toFixed(2), withCommas = v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => (k === 'amount_no_decimals' ? withCommas.split('.')[0] : withCommas));
  }

  // ------------------------------------------------------------------------------ uploads
  // What the Studio may do with an uploaded image. The classifier (AI, /api/classify) runs as soon
  // as the image is added; the shopper can correct it, and a shopper's answer wins.
  //   classification: { kind: logo|graphic|photo|other, printableAsIs, confidence, subject, text, source: ai|shopper }
  // Policy: a logo or graphic that is print-ready goes on the garment as it is (idea 1), and may also
  // be restyled. A photo (or anything not print-ready) is NEVER put on the garment directly by a
  // proposal: the ideas are artwork drawn from it. If the classifier failed (no classification),
  // the image is treated as a logo: most uploads are, and the shopper can correct it.
  // This is a proposal policy, not a hard rule: a shopper may still choose "print my photo as it is".
  const CLASS_LABELS = { logo: 'Logo', graphic: 'Artwork', photo: 'Photo', other: 'Picture' };
  function imagePolicy(c) {
    if (!c || !c.kind) return { asIs: true, kind: 'unknown', label: 'Image', note: 'We print it as it is.', source: null };
    const sure = c.source === 'shopper' || (c.confidence == null ? 1 : +c.confidence) >= 0.5;
    const asIs = (c.kind === 'logo' || c.kind === 'graphic') && c.printableAsIs !== false && sure;
    return {
      asIs, kind: c.kind, source: c.source || 'ai', subject: c.subject || '', text: c.text || '',
      label: CLASS_LABELS[c.kind] || 'Image',
      note: asIs ? 'We print it as it is, and suggest new versions of it.' : 'We draw new artwork from it, ready to print.',
    };
  }
  // Make a proposal obey the policy: an "upload" design of a not-as-is image becomes "art" drawn from it.
  function applyImagePolicy(proposal, policy) {
    if (!proposal || !policy || policy.asIs) return proposal;
    return { ...proposal, decorations: proposal.decorations.map((d) => (d.design.kind !== 'upload' ? d : {
      ...d, design: { kind: 'art', prompt: `A bold, flat illustration of ${policy.subject || 'the subject of the reference photo'}, drawn as a one- or two-colour badge.`, src: null, key: '#00ff00', pending: true, fromImage: true, fromPhoto: true },
    })) };
  }

  // The design of a no-AI idea (the AI failed or gave too few). Never placeholder lettering when there is something
  // real to show: the shopper's words, their image as it is, or new art drawn from their photo (the image model is a
  // separate call and usually still works). Only with none of those is it a placeholder, flagged so the Studio asks
  // for the words before showing it.
  const FALLBACK_FONTS = ['graduate', 'bebas', 'pacifico'];
  // Three of the art directions shoppers with a photo pick most (pick-rate table, 2026-10-15).
  const FALLBACK_ART = ['Woodcut / linocut print, bold carved lines', 'Halftone pop poster, bold flat colours and Ben-Day dots', 'Cartoon mascot, thick outlines and flat colours'];
  function fallbackDesign(words, image, i = 0) {
    const w = String(words || '').trim().toUpperCase().slice(0, 40);
    const policy = image ? imagePolicy(image.cls) : null;
    if (image && policy.asIs) return { kind: 'upload', src: image.src, file: image.file, name: image.name };
    if (image) return { kind: 'art', prompt: `${FALLBACK_ART[i % 3]}: ${policy.subject || 'the subject of the reference photo'}. Only the subject, no background scene.`, src: null, key: '#00ff00', pending: true, fromImage: true, fromPhoto: true, caption: '' };
    if (w) return { kind: 'text', text: w, font: FALLBACK_FONTS[i % 3], ink: 'White', inks: 1 };
    return placeholderDesign(i);
  }
  const placeholderDesign = (i = 0) => ({ kind: 'text', text: 'YOUR TEXT', font: FALLBACK_FONTS[i % 3], ink: 'White', inks: 1, placeholder: true });

  // The shopper's caption (Cal, 2026-10-15): when they typed words to print ("Denny", "Lake Days 2026"),
  // every idea carries them. An AI-art design draws them inside the artwork (design.caption + placement +
  // lettering go to the image model apart from the art prompt); a lettering design prints them. The
  // shopper's own image printed as it is stays unchanged. The AI Worker decides caption vs instruction;
  // this enforces it on every idea, including ones the image policy turned into art.
  function applyCaption(proposals, caption) {
    const c = String(caption || '').trim();
    if (!c || !proposals) return proposals;
    return proposals.map((p) => ({ ...p, decorations: p.decorations.map((d) => {
      const D = d.design;
      if (D.kind === 'art' && !D.src) return { ...d, design: { ...D, caption: c, placement: D.placement || 'below' } };
      if (D.kind === 'text') return { ...d, design: { ...D, text: c } };
      return d;
    }) }));
  }
  // "Edit the design" changes or removes the caption inside drawn art with one AI edit (the art itself stays).
  function captionEdit(caption, next) {
    const old = String(caption || '').trim(), nw = String(next || '').trim();
    if (!old) return '';
    return nw
      ? `Replace the words "${old}" with exactly "${nw}", in the same place, size and lettering style. Spell "${nw}" letter for letter. Change nothing else.`
      : `Remove the words "${old}" from the design. Also remove the banner, ribbon, plate or band they sit on: paint that whole area with the flat background colour, so nothing empty is left where the words were. Do not add any other words. Keep the rest of the art exactly as it is: same subject, style, colours, size and position.`;
  }
  // The art style a design was shown or picked in, for the pick-rate table (AI Worker /api/styles).
  function artStyle(design) {
    if (!design) return null;
    if (design.kind === 'upload') return 'your image as is';
    if (design.kind === 'text') return 'lettering only';
    return design.styleKey || 'art (no direction)';
  }

  // When the image is to be printed as it is, idea 1 shows it as it is, whatever the AI returned
  // (2026-10-11: a shopper chose "print it as it is" and got three AI redraws). `upload` is the
  // shopper's image as a design. Keeps idea 1's garment, colour and place.
  // What a design is ABOUT, for Meta's optimiser (Cal, 2026-10-13): each ad set optimises on its own topic event, so
  // Meta learns who makes dog designs (A1) and who makes bride/bachelorette designs (BR1). Read from everything the
  // Studio knows when the ideas appear: the photo's description, the words typed, the AI's read and the idea titles.
  const TOPICS = {
    dog: /\b((?<!hot[ -])dogs?|pupp(?:y|ies)|pups?|pupper|doggo|pooch|canine|k9|retrievers?|labradors?|poodles?|(?:golden|labra|cava|bernedoodle|aussie)?doodles?|goldendoodles?|labradoodles?|bulldogs?|frenchies|frenchie|pugs?|beagles?|corgis?|husk(?:y|ies)|shepherds?|chihuahuas?|dachshunds?|terriers?|pit ?bulls?|pitbulls?|rottweilers?|rottie|schnauzers?|shih ?tzus?|maltese|yorkies?|spaniels?|collies?|great danes?|mastiffs?|bernese|weimaraners?|vizslas?|greyhounds?|whippets?|pomeranians?|havanese|cavapoos?|cockapoos?|newfies?|akitas?|shibas?|bichons?|boston terriers?|cane corsos?|dobermans?|malinois|samoyeds?)\b/i,
    bride: /\b(brides?|bridal|bridesmaids?|bachelorettes?|bach|hen party|hens? (?:do|weekend|night)|weddings?|engaged|engagement|fianc[eé]e?s?|she said yes|said yes|mrs\.?|bride squad|bride tribe|maid of honou?r|matron of honou?r|newlyweds?|honeymoon|last fling|veils?|i do)\b/i,
  };
  function topics(parts) {
    const t = (parts || []).filter(Boolean).map(String).join(' \n ');
    return Object.keys(TOPICS).filter((k) => TOPICS[k].test(t));
  }

  function enforceAsIs(proposals, policy, upload) {
    if (!policy || !policy.asIs || !upload || !proposals || !proposals.length) return proposals;
    if (proposals.some((p) => p.decorations.some((d) => d.design.kind === 'upload'))) return proposals;
    const [first, ...rest] = proposals;
    return [{ ...first, title: 'Your image, as it is', pitch: 'Printed exactly as you sent it.', decorations: [{ ...first.decorations[0], design: upload }] }, ...rest];
  }
  // Lettering from the AI, cleaned: no placeholders, no runaway repeats, at most 6 lines, and no year or date the
  // shopper did not give. A removed line takes its dangling lead-in with it ("CLASS OF" without
  // "2025" was printed on a card, 2026-10-11). Returns '' when nothing usable is left.
  function cleanLettering(text, brief) {
    const said = String(brief || '');
    // Notes the model leaves for itself are not lettering: "My Best Friend [Paw Print Art Here]" was
    // printed on a card (2026-10-12). Bracketed text and "insert …" / "your text here" go.
    const PLACEHOLDER = /\[[^\]]*\]?|\{[^}]*\}?|<[^>]*>?|\((?:insert|add|your|name|pet|logo|art|image|photo|text)[^)]*\)?|\b(insert (?:name|text|logo)|placeholder|your text( here)?|text here|name here|lorem ipsum)\b/gi;
    let lines = String(text || '').replace(PLACEHOLDER, '').replace(/(.)\1{3,}/g, '$1$1').split(/\n|\\n/).map((l) => l.replace(/\s{2,}/g, ' ').trim()).filter(Boolean);
    let dropped = false;
    lines = lines.map((l) => {
      const years = l.match(/\b(19|20)\d\d\b/g) || [];
      const bad = years.filter((y) => !said.includes(y));
      if (!bad.length) return l;
      dropped = true;
      const rest = bad.reduce((a, y) => a.replace(y, ''), l).replace(/[\s'’.,·•|-]+$/g, '').replace(/^[\s'’.,·•|-]+/g, '').trim();
      return rest;
    }).filter(Boolean);
    if (dropped) {
      const DANGLING = /\b(of|est\.?|established|since|class(\s+of)?|in|the|and|for|from|to|circa|ca\.?)$/i;
      lines = lines.map((l) => l.replace(DANGLING, '').trim()).filter((l) => l && !DANGLING.test(l));
    }
    return lines.slice(0, 6).join('\n').slice(0, 120);
  }

  // ------------------------------------------------------------------------------ lettering (2026-10-19 revamp Phase 3)
  // The lettering screen's words: at most three lines as the shopper typed them. Typed as one long line (the words door),
  // the words break at spaces into lines of about 18 characters, still at most three.
  const LETTER_LINES = 3, LETTER_MAX = 30;
  function letterLines(text) {
    let lines = String(text || '').split(/\n|\\n/).map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean);
    if (lines.length === 1 && lines[0].length > 22) {
      lines = lines[0].split(' ').reduce((acc, w) => { const last = acc[acc.length - 1]; if (last != null && (last + ' ' + w).length <= 18) acc[acc.length - 1] = last + ' ' + w; else acc.push(w); return acc; }, []);
      if (lines.length > LETTER_LINES) lines = [...lines.slice(0, LETTER_LINES - 1), lines.slice(LETTER_LINES - 1).join(' ')];
    }
    return lines.slice(0, LETTER_LINES).map((l) => l.slice(0, LETTER_MAX));
  }
  // A roster: one name per line, with a number in front or behind when the shopper gives one ("23 Smith", "Smith 23",
  // "#7 Ortiz", "Lee, 12"). → [{ name, number }], at most 60 names. A line that is only a number is a name-less number.
  const ROSTER_MAX = 60, NAME_MAX = 24;
  function parseRoster(text) {
    return String(text || '').split(/\n|\\n/).map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean).slice(0, ROSTER_MAX).map((l) => {
      let m = l.match(/^#?(\d{1,3})[\s,.:-]+(.+)$/) || null, number = '', name = l;
      if (m) { number = m[1]; name = m[2]; }
      else if ((m = l.match(/^(.+?)[\s,]+#?(\d{1,3})$/))) { name = m[1]; number = m[2]; }
      else if ((m = l.match(/^#?(\d{1,3})$/))) { name = ''; number = m[1]; }
      return { name: name.replace(/[,\s]+$/, '').slice(0, NAME_MAX), number };
    });
  }

  // Per-piece names and numbers (Phase 3 item 3): one line per piece, "size name number" in any order ("M Smith 23",
  // "Smith 23 XL", "2XL, Lee"). The size must be one the garment has (XXL = 2XL, XXXL = 3XL). → [{ size, name, number, ok }],
  // at most 100 pieces; ok is false when the size is missing or unknown.
  const PIECES_MAX = 100;
  const SIZE_ALIAS = { XXL: '2XL', XXXL: '3XL', XXXXL: '4XL', SM: 'S', SMALL: 'S', MED: 'M', MEDIUM: 'M', LG: 'L', LARGE: 'L', XLG: 'XL' };
  function parsePieces(text, sizes) {
    const known = new Map((sizes || []).map((s) => [String(s).toUpperCase(), s]));
    const sizeOf = (t) => { const u = String(t).toUpperCase().replace(/[.,]/g, ''); return known.get(u) || known.get(SIZE_ALIAS[u] || '') || null; };
    return String(text || '').split(/\n|\\n/).map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean).slice(0, PIECES_MAX).map((l) => {
      const toks = l.split(/[\s,]+/).filter(Boolean);
      let size = null, i = toks.findIndex((t) => sizeOf(t));
      if (i >= 0) { size = sizeOf(toks[i]); toks.splice(i, 1); }
      const r = parseRoster(toks.join(' '))[0] || { name: '', number: '' };
      return { size, name: r.name, number: r.number, ok: !!size && !!(r.name || r.number) };
    });
  }
  const countSizes = (pieces) => pieces.reduce((a, x) => ((a[x.size] = (a[x.size] || 0) + 1), a), {});

  // ------------------------------------------------------------------------------ screens
  // A screen module:
  //   id         unique string
  //   kind       'core' | 'side'
  //   fills      slot names it can fill
  //   priority   number; higher wins among relevant core screens (and sorts offers)
  //   relevant(snapshot, ctx) → boolean
  //   terminal?  true = never drops out when its slots are confirmed (the review/checkout screen)
  //   offer?     side screens: { label, hint(snapshot, ctx) } for the offer list
  //   render(host, api) → paints UI into host; api.emit(emission) ends the visit. Emissions:
  //     { type: 'updates',   updates: [{ slot, value, status?, source? }] }
  //     { type: 'proposals', proposals: [...] | null, read? }   (null = discard proposals)
  //     { type: 'handoff',   to: 'core' | 'checkout' | 'cart' | 'screen', screen?, reason? }
  const SCREEN_FIELDS = ['id', 'kind', 'fills', 'priority', 'relevant', 'render'];
  const registry = [];
  function register(screen) {
    const missing = SCREEN_FIELDS.filter((f) => screen[f] == null);
    if (missing.length) throw new Error(`screen ${screen.id || '?'} is missing ${missing.join(', ')}`);
    if (!['core', 'side'].includes(screen.kind)) throw new Error(`screen ${screen.id}: kind must be core or side`);
    const bad = screen.fills.filter((f) => !SLOTS.includes(f));
    if (bad.length) throw new Error(`screen ${screen.id}: unknown slots ${bad.join(', ')}`);
    const i = registry.findIndex((s) => s.id === screen.id);
    if (i >= 0) registry.splice(i, 1);
    registry.push(screen);
    return screen;
  }
  const screens = () => registry.slice();
  const screen = (id) => registry.find((s) => s.id === id) || null;

  // What a screen sees: a frozen copy of the slots, the effective values (defaults filled in),
  // the proposals and the brief. Screens never mutate state; they emit.
  function snapshot(state, cat) {
    return deepFreeze({ slots: clone(state.slots), effective: clone(effective(state, cat)), proposals: clone(state.proposals), read: state.read, brief: { text: state.brief.text, hasImage: !!state.brief.image, image: state.brief.image ? imagePolicy(state.brief.image.cls) : null }, gates: gates(state, cat) });
  }
  function clone(o) { return o == null || typeof o !== 'object' || o instanceof (root.Blob || class {}) ? o : Array.isArray(o) ? o.map(clone) : Object.fromEntries(Object.entries(o).map(([k, v]) => [k, k === 'src' || k === 'file' || k === 'img' ? v : clone(v)])); }
  function deepFreeze(o) { if (o && typeof o === 'object' && !Object.isFrozen(o) && !(root.Blob && o instanceof root.Blob)) { Object.freeze(o); Object.values(o).forEach(deepFreeze); } return o; }

  function route(state, cat, ctx) {
    const snap = snapshot(state, cat);
    const ok = (s) => { try { return !!s.relevant(snap, ctx); } catch (e) { return false; } };
    const relevant = registry.filter(ok);
    const done = (s) => !s.terminal && s.fills.length > 0 && s.fills.every((k) => confirmed(snap.slots[k]));
    const core = relevant.filter((s) => s.kind === 'core' && !done(s)).sort((a, b) => b.priority - a.priority)[0] || null;
    const offers = relevant.filter((s) => s.kind === 'side' && s.offer).sort((a, b) => b.priority - a.priority);
    const side = state.side ? screen(state.side) : null;
    return { screen: side || core, core, offers, snap };
  }

  // ------------------------------------------------------------------------------ presets
  // URL → slot updates (source 'preset', status 'confirmed'), plus context. Unknown values are
  // dropped with a note rather than failing the page. Parameters:
  //   style=<handle | hoodie | tee | crew | blanket | qzip | vest>   color=<colorway>
  //   method=digital|screen (also cd, dtf, sp)   qty=<n> (split over sizes)   sizes=S:4,M:8
  //   sample=1   location=FC|LC|FB…   text=<lettering> (+ font, ink)   brief=<words for the box>
  //   src | utm_content = the ad (kept in ctx.entry.ad)
  function presetsFromUrl(params, cat, fallbackHandle) {
    const g = (k) => { const v = params.get(k); return v == null || v === '' ? null : v; };
    const updates = [], notes = [];
    const src = (u) => ({ status: 'confirmed', source: 'preset', ...u });
    let p = null;
    const style = g('style');
    if (style) {
      const fam = ALIASES[style.toLowerCase()];
      // A family alias picks that family's cheapest adult garment (youth only when asked for).
      const youth = /youth|kid/i.test(style);
      p = product(cat, style) || (fam && cat.products.filter((x) => x.family === fam && youth === /youth/i.test(x.title)).sort((a, b) => a.minPrice - b.minPrice)[0]) || null;
      if (!p) notes.push(`style "${style}" not found`);
    }
    if (!p && fallbackHandle) p = product(cat, fallbackHandle);
    if (p) updates.push(src({ slot: 'style', value: p.handle }));
    const color = g('color');
    if (color && p) {
      const c = colorNames(p).find((n) => n.toLowerCase() === color.toLowerCase());
      if (c) updates.push(src({ slot: 'colorway', value: c })); else notes.push(`color "${color}" not on ${p.title}`);
    }
    const m = (g('method') || '').toLowerCase();
    const method = { digital: 'digital', cd: 'digital', dtf: 'digital', 'color-direct': 'digital', screen: 'screen', sp: 'screen', 'screen-print': 'screen' }[m];
    if (method) updates.push(src({ slot: 'method', value: method }));
    if (g('sample') === '1' || g('sample') === 'yes') updates.push(src({ slot: 'sample', value: true }));
    const sizes = g('sizes'), qty = parseInt(g('qty') || '', 10);
    if (sizes && p) updates.push(src({ slot: 'sizes', value: Object.fromEntries(sizes.split(',').map((x) => x.split(':')).filter((x) => x.length === 2).map(([s, n]) => [s.trim().toUpperCase(), +n])) }));
    else if (qty > 0 && p) updates.push(src({ slot: 'sizes', value: split(p, (updates.find((u) => u.slot === 'colorway') || {}).value || defaultColor(p), qty) }));
    const text = g('text');
    if (text && p) {
      const loc = mapLocation(p, (g('location') || 'FC').toUpperCase());
      if (loc) updates.push(src({ slot: 'decorations', value: [{ location: loc, design: { kind: 'text', text: text.slice(0, 80), font: g('font') || 'graduate', ink: g('ink') || null } }] }));
    }
    const entry = {
      source: g('src') || g('utm_content') ? 'ad' : params.get('from') === 'pdp' ? 'pdp' : [...params.keys()].some((k) => ['style', 'color', 'method', 'qty', 'sizes', 'sample', 'text'].includes(k)) ? 'link' : 'direct',
      ad: g('src') || g('utm_content') || null,
      brief: g('brief'),
      location: g('location'),
    };
    return { updates, notes, entry };
  }

  const api = {
    SLOTS, STATUSES, SOURCES, METHODS, ALIASES,
    makeCatalog, buildTypes, family, product, colorNames, defaultColor, defaultSize, methodType, mapLocation,
    createState, validate, apply, defaults, effective, quote, gates, split, breakFor,
    register, screens, screen, route, snapshot, presetsFromUrl, imagePolicy, applyImagePolicy, isPlaceholder, placeholderOnly, fallbackDesign, placeholderDesign, enforceAsIs, topics, cleanLettering, colorAvailable, availableColors,
    money, setMoneyFormat: (f) => { if (f) moneyFormat = f; },
    filled, confirmed,
  };
  api.isPlaceholder = isPlaceholder; // own line: keeps the export list above free of merge conflicts
  api.artStyle = artStyle;
  api.applyCaption = applyCaption;
  api.captionEdit = captionEdit;
  api.deliveredPrice = deliveredPrice;
  api.shipDay = shipDay;
  api.letterLines = letterLines;
  api.parseRoster = parseRoster;
  api.parsePieces = parsePieces;
  api.countSizes = countSizes;
  api.sortSizes = sortSizes;
  root.StudioCore = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
